import { useEffect, useState, useRef } from 'react'
import type { FormEventHandler } from 'react'
import { Link, useActionData, useLoaderData, useNavigate } from 'react-router';
import type { ActionFunction, LoaderFunctionArgs, MetaFunction } from 'react-router';

import {
  AddressModel,
  ProductModelJSON,
  PhoneNumberModel,
} from '~/models'
import { ProductUseCase } from '~/usecases/user/product.server'
import { Button } from '~/components/buttons'
import {
  CartForm,
  InquiryForm,
  ProductDetail,
  ProductImages,
} from '~/components/product'
import { MailService } from '~/external-services/mailService.server'
import { inquiry } from '~/templates/user/inquiry'
import { createMeta } from '~/utils/application'
import { getAppAssetsBaseURL, getAppInfoEmail } from '~/utils/application.server'
import { path } from '~/constants'
import { useUserContext } from '~/routes/_user'

const nameInput = 'nameInput'
const emailInput = 'emailInput'
const phoneInput = 'phoneInput'
const contentInput = 'contentInput'
const stockInput = 'stockInput'

const createTitle = (product: ProductModelJSON): string => {
  switch (product.type) {
    case 0: return product.bicycle?.name ?? ''
    case 1: return product.part?.name ?? ''
    case 2: return product.supply?.name ?? ''
  }
  return ''
}

const createKeyword = (product: ProductModelJSON): string => {
  switch (product.type) {
    case 0: return product?.bicycle?.maker.name ?? ''
    case 1: return product?.part?.maker.name ?? ''
    case 2: return product?.supply?.maker.name ?? ''
  }
  return ''
}

export const meta: MetaFunction<typeof loader> = args => {
  const product = args.data?.data.product
  if (!product)
    throw new Error('商品の取得に失敗しました。')
  if (!product.shop?.address)
    throw new Error('ショップに住所が登録されていません。')
  const address = AddressModel.fromJSON(product.shop.address)
  const title = createTitle(product)
  const keyword = createKeyword(product)
  return createMeta({
    ...args,
    title: `${address.prefectureCode.getDisplayName()}${address.city} | ${createTitle(product)} | ${product.shop.name}`,
    description: product.description,
    keywords: keyword,
    isShortedTitle: true,
  })
}

export const loader = async ({ params }: LoaderFunctionArgs) => {
  if (!params.displayID) throw new Error('プロダクトIDが指定されていません')
  const usecase = new ProductUseCase(params.displayID)
  const product = await usecase.findProduct()
  const account = await usecase.findAccount()
  return {
    data: {
      product: product,
      assetBaseURL: getAppAssetsBaseURL(),
      account: account
    }
  }
}

type ActionData = {
  data: {
    success: boolean
  }
  error?: Error
}

export const action: ActionFunction = async ({ params, request }) => {
  if (!params.displayID) throw new Error('プロダクトIDが指定されていません')
  const usecase = new ProductUseCase(params.displayID)
  const product = await usecase.findProduct()
  if (!product.shop?.contact?.email)
    throw new Error('ショップにEメールアドレスが登録されていません。')
  const formData = await request.formData()
  const name = formData.get(nameInput) as string
  const email = formData.get(emailInput) as string
  const phone = formData.get(phoneInput) as string
  const content = formData.get(contentInput) as string
  const emailTemplate = inquiry({
    email: email,
    name: name,
    phoneNumber: phone,
    content: content
  })
  const mailService = MailService.getInstance()
  mailService.createConnection()
  await mailService.sendMail('requestID', {
    to: product.shop.contact.email,
    bcc: getAppInfoEmail(),
    subject: 'お問合せがございます',
    html: emailTemplate.html,
    text: emailTemplate.text,
    replyTo: email,
  })
  return { data: { success: true } }
}

export default function Product() {
  const loaderData = useLoaderData<typeof loader>()
  const actionData = useActionData<ActionData>()
  const [product, setProduct] = useState<ProductModelJSON>()
  const [buttonTitle, setButtonTitle] = useState<string>('内容を送信する')
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false)
  const formRef = useRef<HTMLFormElement>(null)
  const navigation = useNavigate()

  const onSubmit: FormEventHandler = event => {
    if (!confirm('お問い合わせを送信しますか？'))
      return
    setButtonTitle('送信中')
    setButtonDisabled(true)
  }

  useEffect(() => {
    const product = loaderData?.data?.product
    if (product) {
      setProduct(product)
    }
  }, [loaderData])

  useEffect(() => {
    if (!actionData || !formRef.current) return
    if (actionData.data.success) {
      window.alert('送信が完了致しました')
      setButtonTitle('送信完了')
      formRef.current.reset()
    }
  }, [actionData])

  if (!loaderData?.data?.assetBaseURL
    || (product?.type === 0 && !product?.bicycle)
    || (product?.type === 1 && !product?.part)
    || (product?.type === 2 && !product?.supply)
    || !product?.shop?.address
    || !product?.shop?.contact
  ) return null

  return (
    <div className="flex flex-wrap">
      <div className="w-full md:basis-1/2 md:w-1/2 h-auto my-auto">
        {product.files && (
          <ProductImages
            files={product.files}
            assetBaseURL={loaderData.data.assetBaseURL}
          />
        )}
      </div>
      <div className="w-full md:basis-1/2 md:w-1/2 h-auto">
        <ProductDetail product={product} />
        {loaderData.data.account.isPaymentOnboarded
          && product.cartEnabled
          && product.price !== undefined
          && product.price > 0
          && product.stock > 0
          && (
            <>
              <div className="my-4">
                <CartForm
                  product={product}
                  stockInput={stockInput}
                  formProps={{}}
                />
              </div>
              <div className="text-right">
                <Link
                  to={path.user.help.howToBuy}
                  className="text-blue-800 underline italic"
                >購入の流れ</Link>
              </div>
            </>
          )}
      </div>
      <div className="basis-full mt-4 px-8 py-4">
        <div className="rounded p-4">
          <pre className="break-all whitespace-pre-wrap">{product.description}</pre>
        </div>
      </div>
      {product.type === 0 && product.bicycle && product.bicycle.maker.description && (
        <div className="basis-full mt-4 px-8 py-4">
          <h2 className="">{product.bicycle.maker.name}</h2>
          <div className="rounded p-4">
            <pre className="break-all whitespace-pre-wrap">{product.bicycle.maker.description}</pre>
          </div>
        </div>
      )}
      <div className="basis-full mt-4 px-8 pt-4 pb-2">
        <h2 className="">{product.shop.name}</h2>
        <div className="rounded p-4">
          <pre className="break-all whitespace-pre-wrap">{product.shop.description}</pre>
        </div>
        {product.shop.files && product.shop.files.length > 0 && (
          <img className="my-4 mx-auto max-h-72 md:max-h-96" src={`${loaderData.data.assetBaseURL}/${product.shop.files[0].path}`} />
        )}
      </div>
      <p className="basis-full text-sm font-bold text-center">
        ＊「サイクルミッケを見た」とお伝えください。<br />
        ({product.shop.name}に繋がります)
      </p>
      <div className="basis-full mt-4 flex flex-row justify-around flex-wrap">
        <div className="rounded bg-gray-100 my-2 p-4 basis-full md:basis-5/12 flex flex-wrap">
          <div className="justify-center flex items-center mb-4 basis-full mb-auto">
            <img src="/images/svg/icon/phone.svg" className="-mt-0.5 mr-2" />
            <h4 className="text-center">電話で問い合わせ</h4>
          </div>
          <a href={`tel:${product.shop.contact.phoneNumber}`} className="text-center text-2xl basis-full mb-auto">
            {PhoneNumberModel.format(product.shop.contact.phoneNumber)}
          </a>
        </div>
        {product.shop.contact.line &&
          <div className="rounded bg-gray-100 my-2 p-4 basis-full md:basis-5/12">
            <div className="justify-center flex items-center mb-4">
              <img src="/images/svg/icon/line.svg" className="-mt-0.5 mr-2" />
              <h4 className="text-center">LINEで問い合わせ</h4>
            </div>
            <div className="flex justify-center w-full">
              <img
                src={`${loaderData.data.assetBaseURL}/${product.shop.contact.line.qrCodeFile.path}`}
                className="w-24"
              />
            </div>
          </div>
        }
      </div>
      <div className="basis-full mt-2 p-2">
        <h3 className="text-center">以下からメールでの問い合わせが可能です</h3>
        <InquiryForm
          formRef={formRef}
          onSubmit={onSubmit}
          inputs={{
            name: nameInput,
            email: emailInput,
            tel: phoneInput,
            content: contentInput,
          }}
          buttonTitle={buttonTitle}
          buttonDisabled={buttonDisabled}
        />
        <a className="basis-full flex justify-center my-8">
          <Button
            className="w-full md:w-1/2"
            onClick={() => { navigation(-1) }}
          >
            戻る
          </Button>
        </a>
      </div>
    </div>
  )
}
